import { ref, computed } from 'vue'
import { getI18n } from '@/plugins/i18n'

import * as dataGrid from '@/composables/data-grid'

import { useGarageStore } from '@/stores/garage.store'
import { useWebSiteStore } from '@/stores/web-site.store'
import { minutesToHHmm } from '@/lib/utils'

export const useProcessingRow = ({
  gridRef,
  selectedRows,
  setCellValueByField = () => {},
  documentInterventionTypeRef = () => null,
  hasMechanics = true,
  hasSuggestedTime = true,
}) => {
  const i18n = getI18n()
  const garageStore = useGarageStore()
  const webSiteStore = useWebSiteStore()

  const isProcessingRow = ref(false)

  const getTopLevelRowNodes = () => gridRef.value?.instance?.getRootNode()?.children ?? []
  const getProcessingParentRows = () => getTopLevelRowNodes().map(node => node.data).filter(row => row.intervention_type_ref != null)

  const getSuggestedTimeForParent = (parentId, excludedRowId = null) => {
    const parentNode = getTopLevelRowNodes().find(node => node.data?.id === parentId)
    return (parentNode?.children ?? []).reduce((total, node) => {
      const isExcludedRow = node.data?.id === excludedRowId
      const isLaborRow = node.data?.document_row_type === webSiteStore.documentRowTypesEnum.LABOR
      if (isExcludedRow || isLaborRow) return total
      return total + Number(node.data?.suggested_time ?? 0)
    }, 0)
  }

  const setSuggestedTimeForLabor = (documentRowType, parentId = undefined, rowData = undefined) => {
    if (!hasSuggestedTime || documentRowType !== webSiteStore.documentRowTypesEnum.LABOR) return

    const editingRow = gridRef.value?.instance?.getVisibleRows()?.find(row => row.isEditing)?.data
    const resolvedParentId = parentId === undefined
      ? editingRow?.processing_parent_id ?? editingRow?.parent_id ?? null
      : parentId
    const suggestedTime = getSuggestedTimeForParent(resolvedParentId, rowData?.id ?? editingRow?.id)
    if (rowData) rowData.suggested_time = suggestedTime
    else setCellValueByField('suggested_time', suggestedTime)
  }

  const hasCarServiceProcessing = () => {
    const interventionTypes = garageStore.getLocalizedInterventionTypes()
    return getProcessingParentRows().some(row => {
      const interventionType = interventionTypes.find(i => i.id === row.intervention_type_ref)
      return interventionType?.has_car_service || interventionType?.code === webSiteStore.interventionTypeCodeEnum.CAR_SERVICE
    })
  }

  const getProcessingFormItem = (isRoundingExpenseRow, openRoundingRow) => dataGrid.isAdding.value && !isRoundingExpenseRow.value && !openRoundingRow.value ? { dataField: 'intervention_type_ref', colSpan: 6 } : null

  const processingItems = () => {
    const interventionTypes = garageStore.getLocalizedInterventionTypes()

    return [
      {
        key: i18n.t('common.presentProcessing'),
        items: getProcessingParentRows().map(row => {
          const interventionType = interventionTypes.find(i => i.id === row.intervention_type_ref)
          return {
            value: `present:${row.id}`,
            interventionTypeRef: row.intervention_type_ref,
            parentId: row.id,
            name: `${interventionType?.name ?? ''} - ${row.description ?? ''}`,
          }
        }),
      },
      {
        key: i18n.t('common.newProcessing'),
        items: interventionTypes.map(i => ({
          value: `new:${i.id}`,
          interventionTypeRef: i.id,
          parentId: null,
          name: i.name,
        })),
      },
    ]
  }

  const onProcessingSelectionChanged = (e) => {
    setCellValueByField('processing_parent_id', e.parentId)
    const editingRow = gridRef.value?.instance?.getVisibleRows()?.find(row => row.isEditing)?.data
    setSuggestedTimeForLabor(editingRow?.document_row_type, e.parentId)
  }

  const documentRowTypeChipData = (row) => {
    const interventionType = row.intervention_type_ref != null ? garageStore.getLocalizedInterventionTypes().find(i => i.id === row.intervention_type_ref) : null
    return interventionType ?? webSiteStore.getLocalizedDocumentRowTypes().find(x => x.id === row.document_row_type)
  }

  const proposedProcessing = () => {
    const flatRows = (nodes) => { return nodes.flatMap(node => [node, ...flatRows(node.children ?? [])]) }
    const latestDetailRow = flatRows(getTopLevelRowNodes())
      .filter(node => node.parent?.data?.intervention_type_ref != null)
      .reduce((max, node) => (!max || new Date(node.data.created_at) > new Date(max.data.created_at) ? node : max), null)

    return {
      interventionTypeRef: latestDetailRow?.parent?.data?.intervention_type_ref ?? documentInterventionTypeRef() ?? null,
      parentId: latestDetailRow?.parent?.data?.id ?? null,
    }
  }

  const getProcessingEditColumns = () => [
    {
      itemType: 'group',
      colSpan: 2,
      items: [
        {
          itemType: 'group',
          caption: i18n.t('common.mainInfo'),
          name: 'mdi-view-list',
          captionTemplate: 'generic-caption-template',
          colCount: 12,
          items: [
            { dataField: 'intervention_type_ref', label: { text: i18n.t('common.documentRowType') }, colSpan: 12 },
            { dataField: 'description', colSpan: 12 },
            ...(hasMechanics ? [{ dataField: 'mechanics', colSpan: 12 }] : []),
            ...(hasMechanics ? [
              { dataField: 'partial_quantity_minutes', label: { text: i18n.t('common.billableTime') }, colSpan: 4, editorOptions: { readOnly: true, format: minutesToHHmm } },
              { dataField: 'partial_actual_duration', label: { text: i18n.t('common.actualDuration') }, colSpan: 4, editorOptions: { readOnly: true, format: minutesToHHmm } },
              { dataField: 'partial_suggested_time', label: { text: i18n.t('common.suggestedTime') }, colSpan: 4, editorOptions: { readOnly: true, format: minutesToHHmm } },
            ] : []),
          ]
        }
      ]
    },
    { itemType: 'group', colSpan: 3, items: [] },
  ]

  const getTimeEditTab = (documentRowType) => {
    const isLaborRow = documentRowType === webSiteStore.documentRowTypesEnum.LABOR
    const hasSuggestedTime = [
      webSiteStore.documentRowTypesEnum.PRODUCT,
      webSiteStore.documentRowTypesEnum.FREE,
      webSiteStore.documentRowTypesEnum.LABOR,
      webSiteStore.documentRowTypesEnum.TEXT,
    ].includes(documentRowType)

    if (!hasSuggestedTime) return null

    return {
      title: i18n.t('common.times'),
      items: [{
        itemType: 'group',
        caption: i18n.t('common.times'),
        colCount: 2,
        items: [
          ...(isLaborRow ? [{ dataField: 'actual_duration', label: { text: i18n.t('common.timeClock') }, editorOptions: { min: 0, readOnly: true, format: minutesToHHmm } }] : []),
          { dataField: 'suggested_time', editorOptions: { readOnly: true, format: minutesToHHmm } },
          ...(isLaborRow ? [
            { dataField: 'operationCode', editorOptions: { readOnly: true } },
            { dataField: 'recognizedTime', editorOptions: { readOnly: true } },
          ] : []),
        ],
      }],
    }
  }

  const hideSaveAndNewForProcessingRow = (e) => {
    if (!isProcessingRow.value) return

    const toolbarItems = e.component.option('toolbarItems') || []
    e.component.option('toolbarItems', toolbarItems.map(item => (item.options?.text === i18n.t('common.saveAndNew') ? { ...item, visible: false } : item)))
  }

  const onProcessingInitNewRow = (e, openRoundingRow) => {
    isProcessingRow.value = false

    if (!openRoundingRow.value) {
      const processing = proposedProcessing()
      e.data.intervention_type_ref = processing.interventionTypeRef
      e.data.processing_parent_id = processing.parentId
    }
  }

  const onProcessingEditingStart = (e) => { isProcessingRow.value = e.data?.intervention_type_ref != null }

  const showProcessingDetailRow = async(grid, detailNode) => {
    const parentKey = detailNode?.parent?.data?.intervention_type_ref != null ? detailNode.parent.key : null
    if (parentKey == null) return

    grid.option('expandedRowKeys', [parentKey])
    await grid.navigateToRow(detailNode.key)
    grid.option('focusedRowKey', detailNode.key)
    grid.focus()
  }

  const expandProcessingParentOnRowInserted = async(e) => {
    const grid = e.component
    let detailNode = grid.getNodeByKey(e.key)
    if (detailNode?.parent?.data?.intervention_type_ref == null) {
      await grid.refresh()
      detailNode = grid.getNodeByKey(e.key)
    }

    await showProcessingDetailRow(grid, detailNode)
  }

  const refreshAndExpandLatestProcessingParent = async() => {
    const grid = gridRef.value?.instance
    if (!grid) return

    await grid.refresh()

    const processing = proposedProcessing()
    const parentNode = processing.parentId != null
      ? getTopLevelRowNodes().find(node => node.data?.id === processing.parentId)
      : getTopLevelRowNodes().find(node => node.data?.intervention_type_ref === processing.interventionTypeRef)
    const detailNode = parentNode?.children?.slice(-1)?.[0]
    await showProcessingDetailRow(grid, detailNode)
  }

  const onProcessingDragChange = (e) => {
    if (!e.dropInsideItem) return

    const visibleRows = e.component.getVisibleRows()
    const fromRow = visibleRows[e.fromIndex]
    const toRow = visibleRows[e.toIndex]

    const isProcessingTarget = toRow?.data?.intervention_type_ref != null
    const isProcessingSource = fromRow?.data?.intervention_type_ref != null
    const isRootOnlySource = fromRow?.data?.is_rounding_row || fromRow?.data?.is_rounding_expense_row
    const hasChildren = (fromRow?.node?.children?.length ?? 0) > 0

    if (!isProcessingTarget || isProcessingSource || isRootOnlySource || hasChildren) { e.cancel = true }
  }

  const exportRows = computed(() => {
    const grid = gridRef.value?.instance
    const rowsById = new Map()
    for (const row of selectedRows.value) {
      if (row.intervention_type_ref != null) {
        for (const child of (grid?.getNodeByKey(row.id)?.children ?? [])) { rowsById.set(child.data.id, child.data) }
      } else {
        rowsById.set(row.id, row)
      }
    }
    return [...rowsById.values()]
  })

  const batchDeleteRowIds = computed(() => {
    const explicitlySelectedIds = selectedRows.value.map(row => row.id)
    return gridRef.value?.instance?.getSelectedRowKeys('all') ?? explicitlySelectedIds
  })

  const processingTreeListOptions = {
    remoteOperations: 'false',
    parentIdExpr: 'parent_id',
    dataStructure: 'plain',
    selection: {
      ...dataGrid.useDefaultGridSelection(),
      recursive: true,
    },
    paging: {
      enabled: false
    },
  }

  return {
    isProcessingRow,
    processingItems,
    hasCarServiceProcessing,
    onProcessingSelectionChanged,
    setSuggestedTimeForLabor,
    documentRowTypeChipData,
    getProcessingEditColumns,
    getTimeEditTab,
    getProcessingFormItem,
    hideSaveAndNewForProcessingRow,
    onProcessingInitNewRow,
    onProcessingEditingStart,
    expandProcessingParentOnRowInserted,
    refreshAndExpandLatestProcessingParent,
    onProcessingDragChange,
    exportRows,
    batchDeleteRowIds,
    processingTreeListOptions,
  }
}
